/** @format */

import React, { FC, useEffect, useMemo } from 'react'
import styled from 'styled-components'
import { ConfigProvider, _$, Drawer, Message } from '@king-design/react'
import { getAlarmShields } from 'src/api/ApiAlarmSilences'
import { useAsync } from 'react-use'
import { ShieldTypeOpts, ShieldTimeTypeOpts } from './EditDrawer'
import useGlobalData from 'src/hooks/useGlobalData'
import { GetListSysEvents } from 'src/api/ApiAlarmServer'

interface ViewDrawerProps {
  className?: string
  visible: boolean
  shieldVersion?: string
  onClose: (update?: boolean) => void
}

const ViewDrawer: FC<ViewDrawerProps> = ({ className, visible, shieldVersion, onClose }) => {
  const productList = useGlobalData('productList')
  const { value } = useAsync(async () => {
    if (visible) {
      if (shieldVersion) {
        const result = await getAlarmShields({ ShieldVersion: shieldVersion })
        return result?.AlarmShields?.[0] || {}
      }
    }
  }, [visible, shieldVersion])

  // 获取事件列表
  const { value: eventList } = useAsync(async () => {
    const result = await GetListSysEvents({
      ProductType: 999
    })
    if (result && result?.code === 'Success' && result?.data.eventList) {
      const eventList = result.data.eventList
      return eventList
    } else {
      Message.error(result?.message)
      return null
    }
  }, [])

  // 获取事件名称
  const getEventName = (productType, value) => {
    if (eventList?.length) {
      return (
        eventList.find(item => item.productType === productType && item.name === value)?.nameDesc ||
        ''
      )
    } else {
      return ''
    }
  }

  return (
    <ConfigProvider value={{ classNamePrefix: 'kd' }}>
      <Drawer
        title={_$('查看屏蔽规则')}
        value={visible}
        onClose={() => {
          onClose()
        }}
        width={850}
      >
        {value && productList?.length ? (
          <div className={className}>
            <div className="form_item">
              <p className="form_item_label">{_$('规则名称')}：</p>
              <div className="form_item_content">{value?.ShieldName}</div>
            </div>
            <div className="form_item">
              <p className="form_item_label">{_$('描述')}：</p>
              <div className="form_item_content">{value?.ShieldDescription}</div>
            </div>
            <div className="form_item">
              <p className="form_item_label">{_$('产品类型')}：</p>
              <div className="form_item_content">
                {productList?.find(item => item.productType === value.ProductType)?.productName}
              </div>
            </div>
            <div className="form_item">
              <p className="form_item_label">{_$('屏蔽类型')}：</p>
              <div className="form_item_content">
                {ShieldTypeOpts[value?.ShieldType || 'instance']}
              </div>
            </div>
            <div className="form_item">
              <p className="form_item_label">{_$('屏蔽范围')}：</p>
              <div className="form_item_content">
                {value?.IsThresholdFlag === 1 ? <span>{_$('指标')}</span> : null}
                {value?.IsEventFlag === 1 ? <span>{_$('事件')}</span> : null}
              </div>
            </div>
            {value?.IsThresholdFlag === 1 ? (
              <div className="form_item">
                <p className="form_item_label">{_$('指标')}：</p>
                <div className="form_item_content">
                  {value?.ShieldMetrics?.length
                    ? value?.ShieldMetrics.map(({ MetricDesc, MetricTags }) => {
                        if (MetricTags?.length) {
                          return MetricTags.map(tag => `${MetricDesc}[${tag}]`)?.join('、')
                        }
                        return MetricDesc
                      })?.join('、')
                    : _$('全部指标')}
                </div>
              </div>
            ) : null}
            {value?.IsEventFlag === 1 ? (
              <div className="form_item">
                <p className="form_item_label">{_$('事件')}：</p>
                <div className="form_item_content">
                  {value?.ShieldEvents?.length
                    ? value?.ShieldEvents.map(({ EventName, EventTags }) => {
                        const name = getEventName(value.ProductType, EventName)
                        if (EventTags?.length) {
                          return EventTags.map(
                            tag => `${name}[${tag.TagName}=${tag.TagValue}]`
                          )?.join('、')
                        }
                        return name
                      })?.join('、')
                    : _$('全部事件')}
                </div>
              </div>
            ) : null}
            <div className="form_item">
              <p className="form_item_label">{_$('生效时间范围')}：</p>
              <div className="form_item_content">
                {ShieldTimeTypeOpts[value?.ShieldTimeType || 1]}
              </div>
            </div>
            {value?.ShieldTimeType === 2 ? (
              <div className="form_item">
                <p className="form_item_label">{_$('绝对时间范围')}：</p>
                <div className="form_item_content">
                  {`${value?.EffectStartTime} - ${value?.EffectEndTime}`}
                </div>
              </div>
            ) : null}
            {value?.ShieldTimeType === 3 ? (
              <>
                <div className="form_item">
                  <p className="form_item_label">{_$('循环日期')}：</p>
                  <div className="form_item_content">
                    {value?.EffectStartDte && value?.EffectEndDate
                      ? `${value?.EffectStartDte} - ${value?.EffectEndDate}`
                      : _$('每天循环')}
                  </div>
                </div>
                <div className="form_item">
                  <p className="form_item_label">{_$('循环时间')}：</p>
                  <div className="form_item_content">
                    {`${value?.EffectStartTime} - ${value?.EffectEndTime}`}
                  </div>
                </div>
              </>
            ) : null}
          </div>
        ) : null}
      </Drawer>
    </ConfigProvider>
  )
}

export default styled(ViewDrawer)`
  p {
    margin-bottom: 0;
  }
  .form_item {
    display: flex;
    margin-bottom: 20px;
    .form_item_label {
      width: 100px;
      margin-right: 4px;
      text-align: right;
      font-weight: 700;
    }
    .form_item_content {
      width: calc(100% - 104px);
      span {
        margin-right: 4px;
      }
      p {
        margin-bottom: 10px;
      }
    }
  }
`
